// web/src/api/assist/customer-profile.ts —— 工作台客户画像（外部档案 + 来源编排）
// 拆自 web/src/api/assist.ts 的同名分区（内容原样搬运）；由 web/src/api/assist.ts 以 export * 聚合，导出面对调用方不变。

import { del, get, post, put, type ReqConfig } from '../../request';
// ---------- 工作台客户画像（外部业务系统档案） ----------

/** 来源执行态：success 之外一律视为「该组不可用」，前端隐藏组内数据并提示来源不可用 */
export type ProfileSourceStatus = 'success' | 'business_error' | 'fail' | 'skip' | 'not_found';

/** 分组内的一个展示字段（取不到值的字段后端不下发，前端据此隐藏该行） */
export interface ProfileField {
  /** 组内稳定键（如 bill_day / main_package，映射口径见 docs/guangdian/67-客户综合视图-接口对接清单.md） */
  key: string;
  /** 展示名（配置给的行业文案，缺省时前端按内置标签兜底） */
  label?: string;
  value: string;
}

/** 行内详情：以该行对象为根的子表（如商品行下的服务项 / 资源行下的用量明细） */
export interface ProfileTableDetail {
  title?: string;
  columns: string[];
  rows: string[][];
}

/** 分组内的一张表：columns 为表头（与配置同序），rows 为按列对齐的取值行 */
export interface ProfileTable {
  key?: string;
  title?: string;
  columns: string[];
  rows: string[][];
  /** 行内详情，与 rows **同序**；该行没有详情时为 null（据此决定是否渲染详情入口） */
  details?: (ProfileTableDetail | null)[];
}

/** 分组结果：status 为该组主来源执行态，非 success 时无 fields/tables */
export interface ProfileGroup {
  key: string;
  title?: string;
  status: ProfileSourceStatus | string;
  fields?: ProfileField[];
  tables?: ProfileTable[];
}

/**
 * 一条客户背景：服务端按 `assist.kb_context_fields` 登记表组装，供「智能辅助」随每次提问
 * 送进 WeKnora（`customer_context`，只进生成提示词、不参与检索、不落会话历史）。
 */
export interface KbAssistContextItem {
  /** 登记键（如 city / current_plan / lucky）：WeKnora 侧按 `^[a-z0-9_]{1,32}$` 校验 */
  key: string;
  /** 中文名（「归属地」「当前主套餐」），服务端按登记表下发 */
  label: string;
  value: string;
}

/** 工作台客户画像：外部业务系统（如广电集中BOSS）档案的聚合结果，字段为聚合后归一的展示值 */
export interface CustomerProfile {
  /** 是否聚到至少一项有效档案（false=无外部档案，卡片按空态展示） */
  found: boolean;  /** 已脱敏（前 3 后 4） */
  phone: string;
  /** 姓名（上游通常已脱敏，如 黄**） */
  name: string;
  /** 归属地名称（如 广州） */
  city: string;
  /** 归属地编码（如 GZ） */
  city_code?: string;
  /** 所属公司/集团；个人客户为空 */
  company: string;
  /** 类型码原值（如 00/01） */
  customer_type?: string;
  /** 类型文案（00→个人、01→集团） */
  customer_type_label?: string;
  gender?: string;
  /** 状态码 */
  status?: string;
  /** 状态文案（如 正使用/停机） */
  status_name?: string;
  open_time?: string;
  cust_id?: string;
  serv_id?: string;
  /** 各查询动作执行态：success|business_error|fail|skip|not_found（排查用，不展示） */
  sources?: Record<string, string>;
  /** 分组/表格扩展（加法结构；未配置分组时缺省）：承载话费、资源、在用商品等数组与嵌套数据 */
  groups?: ProfileGroup[];
  /**
   * 客户背景（服务端按登记表组装）：只含取到值的字段。
   * 缺失（undefined）表示服务端没有这个能力，消费方走本地兜底；空数组表示登记表里一条也没命中。
   */
  assist_context?: KbAssistContextItem[];
}

/**
 * 工作台客户画像（GET /assist/customer-profile）：按**明文**号码查外部业务档案，脱敏留痕由服务端负责。
 * `groups` 为分组 key 白名单（逗号拼接下发），用于首屏轻载与页签按需懒加载；不传=返回全部已配置分组。
 * 空号码、后端未配置编排、数据源不可达一律返回 null——消费方允许空态，故不抛错、不弹全局提示，
 * 调用方无需 try/catch。timeout 12000 = 服务端总预算 8s + 4s 余量。
 */
export async function getCustomerProfile(phone: string, groups?: string[]): Promise<CustomerProfile | null> {
  if (!String(phone || '').trim()) return null;
  const params: Record<string, string> = { phone };
  const keys = (groups || []).map((k) => String(k || '').trim()).filter(Boolean);
  if (keys.length) params.groups = keys.join(',');
  try {
    const r = await get<CustomerProfile | null>('/assist/customer-profile', params, { silent: true, timeout: 12000 });
    return r || null;
  } catch {
    return null;
  }
}

// ---------- 工作台客户画像来源编排（坐席辅助设置） ----------

/** 来源类型：外部数据源动作 / 内建本地客户档案（bcc_crm） */
export type ProfileSourceType = 'data_source' | 'builtin';

/** 一个来源声明 */
export interface ProfileSourceSpec {
  id: string;
  type: ProfileSourceType;
  code: string;
}

/** 展示字段的一个取值候选：来源 + 规范化结果路径（source 留空=在所有外部结果中按路径取首个非空） */
export interface ProfileFieldRef {
  source?: string;
  path: string;
}

/** 分组内一个展示字段的声明（refs 为有序候选；单来源场景可用 source + path 简写） */
export interface ProfileGroupFieldSpec {
  key: string;
  label?: string;
  source?: string;
  path?: string;
  refs?: ProfileFieldRef[];
}

/** 分组内一张表的列声明 */
export interface ProfileTableColumnSpec {
  label: string;
  path: string;
}

/** 表格行内详情的声明：rows_path 相对**行对象**，供行内悬浮展示（如套餐包含的服务项） */
export interface ProfileGroupTableDetailSpec {
  title?: string;
  rows_path: string;
  columns: ProfileTableColumnSpec[];
}

/** 分组内一张表的声明：rows_path 指向结果里的列表（支持点分层、数组下标与跨数组展开） */
export interface ProfileGroupTableSpec {
  key?: string;
  title?: string;
  source?: string;
  rows_path: string;
  columns: ProfileTableColumnSpec[];
  /** 可选的行内详情声明；缺省时该表不下发 details */
  detail?: ProfileGroupTableDetailSpec;
}

/** 分组声明：一组展示字段 + 若干张表（单表简写 table 与数组 tables 等价） */
export interface ProfileGroupSpec {
  key: string;
  title?: string;
  /** 组主来源（决定该组 status）；缺省时取首个带来源的字段/表格 */
  source?: string;
  fields?: ProfileGroupFieldSpec[];
  table?: ProfileGroupTableSpec;
  tables?: ProfileGroupTableSpec[];
}

/** 工作台客户画像来源编排（GET/PUT /assist/customer-profile/config） */
export interface WorkbenchProfileConfig {
  sources: ProfileSourceSpec[];
  fields: Record<string, ProfileFieldRef[]>;
  /** 分组/表格声明：行业语义（组 key、字段 key、表格列）只在配置里，前端按组渲染不写死字段名 */
  groups?: ProfileGroupSpec[];
  customer_type_labels?: Record<string, string>;
}

/** GET /assist/customer-profile/config：读取来源编排（已归一，含内建 bcc_crm） */
export function getWorkbenchProfileConfig(): Promise<WorkbenchProfileConfig> {
  return get('/assist/customer-profile/config');
}

/** PUT /assist/customer-profile/config：覆盖保存（来源存在/启用与字段白名单由后端校验） */
export function updateWorkbenchProfileConfig(cfg: WorkbenchProfileConfig): Promise<{ key: string }> {
  return put('/assist/customer-profile/config', cfg);
}

/**
 * POST /assist/customer-profile/config/preview：按号码 dry-run 一次合并取数（不落库），
 * 返回聚合结果与各来源执行态（含分组），供配置界面核对映射是否正确。
 * `groups` 为逗号分隔的分组 key 白名单，可空（= 全部已配置分组）。
 */
export function previewWorkbenchProfile(p: { phone: string; groups?: string; config?: WorkbenchProfileConfig }): Promise<CustomerProfile> {
  return post('/assist/customer-profile/config/preview', p);
}

